import React, { useState, useEffect } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import { isViewer } from "../utils/permissions";

const LeaveRequestPage = () => {
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState("");
  // Per-leave-type quota / used / available, keyed by 'earned'|'casual'|'medical'.
  const [leaveAvailability, setLeaveAvailability] = useState({
    earned: { quota: 0, used: 0, available: 0 },
    casual: { quota: 0, used: 0, available: 0 },
    medical: { quota: 0, used: 0, available: 0 },
  });
  const [leaveType, setLeaveType] = useState("casual");
  const [formData, setFormData] = useState({
    from_date: "",
    to_date: "",
    reason: "",
  });

  // Kitchen users (role_id = "1") can only apply for Casual Leave.
  // Admin / viewer see Earned + Casual. Medical Leave is intentionally hidden
  // for now — unhide later by un-commenting the line below.
  const isKitchenRole = localStorage.getItem("role_id") === "1";
  const leaveTypeOptions = isKitchenRole
    ? [{ value: "casual", label: "Casual Leave (CL)" }]
    : [
      { value: "earned", label: "Earned Leave (EL)" },
      { value: "casual", label: "Casual Leave (CL)" },
      // { value: "medical", label: "Medical Leave (ML)" },
    ];

  const availableForSelectedType = leaveAvailability[leaveType]?.available ?? 0;

  // Days are derived from the range — inclusive of both ends.
  const computedDays =
    formData.from_date && formData.to_date
      ? Math.max(
        0,
        Math.floor(
          (new Date(formData.to_date) - new Date(formData.from_date)) /
          (1000 * 60 * 60 * 24)
        ) + 1
      )
      : 0;

 // Cross-month leave ranges are allowed. Just need to and to_date >= from_date.
  const rangeValid =
    formData.from_date &&
    formData.to_date &&
    new Date(formData.to_date) >= new Date(formData.from_date);
  const [loading, setLoading] = useState(false);
  const userId = localStorage.getItem("user_id");

  useEffect(() => {
    fetchEmployees();
  }, []);

  const fetchEmployees = async () => {
    try {
      // Kitchen role (role_id=1) sees only their own kitchen's employees.
      // Admin (role_id=2) and others see all active employees.
      const roleId = localStorage.getItem("role_id");
      const url =
        roleId === "1"
          ? `https://ims-attendanceback.indevconsultancy.in/api/register-employee?user_id=${userId}`
          : "https://ims-attendanceback.indevconsultancy.in/api/register-employee";
      const res = await axios.get(url);
      if (res.data.status) {
        setEmployees(res.data.employees || []);
      }
    } catch (error) {
      console.error("Error fetching employees:", error);
    }
  };

  const resetAvailability = () =>
    setLeaveAvailability({
      earned: { quota: 0, used: 0, available: 0 },
      casual: { quota: 0, used: 0, available: 0 },
      medical: { quota: 0, used: 0, available: 0 },
    });

  const handleEmployeeChange = async (empId) => {
    setSelectedEmployee(empId);

    if (!empId) {
      resetAvailability();
      return;
    }

    const emp = employees.find((e) => e.empdetailsid == empId);
    if (!emp) {
      resetAvailability();
      return;
    }

    // Per-type availability is keyed by month — use today's month for the
    // initial check. (It re-fetches when from_date is picked, see effect below.)
    const now = new Date();
    const monthYear = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;

    try {
      const res = await axios.get(
        `https://ims-attendanceback.indevconsultancy.in/api/leave-availability/?employee_id=${empId}&kitchen_id=${emp.kithchenid}&month_year=${monthYear}`
      );
      if (res.data.status && res.data.data) {
        setLeaveAvailability(res.data.data);
      } else {
        resetAvailability();
      }
    } catch (error) {
      resetAvailability();
    }
  };

  // When the user picks a from_date, refresh availability for that month
  // (request validation is month-scoped on the backend too).
  useEffect(() => {
    if (!selectedEmployee || !formData.from_date) return;
    const emp = employees.find((e) => e.empdetailsid == selectedEmployee);
    if (!emp) return;
    const monthYear = formData.from_date.slice(0, 7);
    axios
      .get(
        `https://ims-attendanceback.indevconsultancy.in/api/leave-availability/?employee_id=${selectedEmployee}&kitchen_id=${emp.kithchenid}&month_year=${monthYear}`
      )
      .then((res) => {
        if (res.data.status && res.data.data) setLeaveAvailability(res.data.data);
      })
      .catch(() => { });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.from_date, selectedEmployee]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedEmployee) {
      Swal.fire("Error", "Please select an employee", "error");
      return;
    }

    const selectedEmp = employees.find(e => e.empdetailsid == selectedEmployee);
    if (!selectedEmp) return;

    if (!rangeValid) {
      Swal.fire("Error", "Please pick a valid date range (To Date must be on or after From Date)", "error");
      return;
    }
    // Quota check is left to the backend so that kitchens WITHOUT a leave
    // policy can still submit (backend allows in that case). When a policy
    // exists and quota is exceeded, backend will return a 400 with a clear
    // message that gets surfaced in the catch block below.

    setLoading(true);
    try {
      const res = await axios.post(
        "https://ims-attendanceback.indevconsultancy.in/api/leave-adjustment/",
        {
          employee_id: selectedEmployee,
          kitchen_id: selectedEmp.kithchenid,
          from_date: formData.from_date,
          to_date: formData.to_date,
          leave_type: leaveType,
          reason: formData.reason,
          requested_by: userId,
        }
      );

      if (res.data.status) {
        Swal.fire("Success!", res.data.message, "success");
        // Reset form
        setSelectedEmployee("");
        setLeaveType("casual");
        setFormData({
          from_date: "",
          to_date: "",
          reason: "",
        });
        resetAvailability();
      } else {
        Swal.fire("Error", res.data.message, "error");
      }
    } catch (error) {
      console.error("Leave submit failed:", error, error?.response?.data);
      const serverMsg = error?.response?.data?.message;
      const status = error?.response?.status;
      const detail =
        serverMsg ||
        (status ? `HTTP ${status} — ${error.message}` : error.message) ||
        "Failed to submit request";
      Swal.fire("Error", detail, "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="px-3 py-3 bg-cream" style={{ minHeight: "100vh" }}>
      <div className="card">
        <div className="card-header bg-skyBlue text-white">
          <h5 className="mb-0">Request Leave Adjustment</h5>
        </div>
        <div className="card-body">
          <form onSubmit={handleSubmit}>
            <div className="row g-3">
              <div className="col-md-6">
                <label className="form-label">Select Employee *</label>
                <select
                  className="form-select"
                  value={selectedEmployee}
                  onChange={(e) => handleEmployeeChange(e.target.value)}
                  required
                >
                  <option value="">-- Select Employee --</option>
                  {employees.map((emp) => (
                    <option key={emp.empdetailsid} value={emp.empdetailsid}>
                      {emp.employee_code} - {emp.emp_name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-md-6">
                <label className="form-label">Leave Type *</label>
                <select
                  className="form-select"
                  value={leaveType}
                  onChange={(e) => setLeaveType(e.target.value)}
                  required
                >
                  {leaveTypeOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label} — Available: {leaveAvailability[opt.value]?.available ?? 0}
                    </option>
                  ))}
                </select>
                <small className="text-muted">
                  Yearly quota: {leaveAvailability[leaveType]?.quota ?? 0},
                  &nbsp;Used (incl. pending): {leaveAvailability[leaveType]?.used ?? 0},
                  &nbsp;<strong>Available: {availableForSelectedType}</strong>
                </small>
              </div>

              <div className="col-md-4">
                <label className="form-label">From Date *</label>
                <input
                  type="date"
                  className="form-control"
                  value={formData.from_date}
                  onChange={(e) =>
                    setFormData({ ...formData, from_date: e.target.value })
                  }
                  required
                />
              </div>

              <div className="col-md-4">
                <label className="form-label">To Date *</label>
                <input
                  type="date"
                  className="form-control"
                  value={formData.to_date}
                  min={formData.from_date || undefined}
                  onChange={(e) =>
                    setFormData({ ...formData, to_date: e.target.value })
                  }
                  required
                />
              </div>

              <div className="col-md-4">
                <label className="form-label">Leave Days (auto)</label>
                <input
                  type="text"
                  className="form-control"
                  value={computedDays || ""}
                  readOnly
                  style={{ backgroundColor: "#e9ecef", fontWeight: "bold" }}
                />
              </div>

              <div className="col-md-12">
                <label className="form-label">Reason</label>
                <textarea
                  className="form-control"
                  rows="3"
                  value={formData.reason}
                  onChange={(e) =>
                    setFormData({ ...formData, reason: e.target.value })
                  }
                  placeholder="Enter reason for leave adjustment..."
                ></textarea>
              </div>

              <div className="col-md-12">
                <div className="alert alert-info">
                  <strong>Note:</strong> This request will be sent to admin for approval.
                  Credits will only be deducted after approval.
                </div>
              </div>

              <div className="col-md-12">
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={loading}
                >
                  {loading ? "Submitting..." : "Submit Request"}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default LeaveRequestPage;